import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PackagePlus, Plus, Barcode } from "lucide-react";
import { toast } from "sonner";

// Bilinmeyen barkod → hızlı yeni ürün kaydı (ad + marka + birim + fiyat).
// Kaydedince ürünü sisteme ekler (POST /products) ve oluşan ürünü onCreated ile döndürür.
export default function NewProductQuickDialog({ open, barcode = "", onOpenChange, onCreated, title = "Yeni Ürün Ekle" }) {
  const [form, setForm] = useState({ name: "", brand: "", unit: "ADET", price: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ name: "", brand: "", unit: "ADET", price: "" }); }, [open]);

  const save = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Ürün adı gerekli");
    const gross = Number(form.price) || 0;
    const net = gross > 0 ? Math.round((gross / 1.2) * 100) / 100 : 0;   // girilen fiyat KDV dahil → net (+KDV) sakla
    setSaving(true);
    try {
      const brand = form.brand.trim();
      const { data } = await api.post("/products", {
        name, brand, discount_group: brand, unit: (form.unit || "ADET").toUpperCase(),
        barcode: (barcode || "").trim(), list_price: net, price: net, currency: "TL",
        quantity: 0, critical_level: 0, price_kdv_included: false, kdv_rate: 20,
      });
      toast.success("Ürün eklendi: " + data.name);
      onCreated && onCreated(data);
      onOpenChange && onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[96vw] sm:max-w-md z-[80]" data-testid="np-quick-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><PackagePlus className="h-5 w-5 text-primary" />{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border bg-muted/40 px-3 py-2 text-sm flex items-center gap-2" data-testid="np-quick-barcode">
            <Barcode className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground uppercase tracking-wide">Barkod</span>
            <span className="font-semibold font-mono">{barcode || "—"}</span>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Ürün Adı *</Label>
            <Input className="mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="np-quick-name" autoFocus />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Marka</Label>
              <Input className="mt-1" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="np-quick-brand" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Birim</Label>
              <Input className="mt-1" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="np-quick-unit" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Fiyat (KDV dahil)</Label>
              <Input className="mt-1 tabular-nums" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="np-quick-price" />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">Marka/kategori ve diğer ayrıntıları Stok ekranından sonra düzenleyebilirsiniz.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange && onOpenChange(false)} data-testid="np-quick-cancel">Vazgeç</Button>
          <Button onClick={save} disabled={saving} data-testid="np-quick-save"><Plus className="h-4 w-4 mr-1.5" />{saving ? "Ekleniyor..." : "Ekle"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
